import React, { useMemo, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useCart } from "../../context/CartContext";

const MIN_PER_BITE = 15;
const MIN_PEOPLE = 10;

const BITE_ITEMS = [
  {
    id: "kleina-lax",
    name: "Kleina",
    description: "með rjómaostakremi, reyktum lax og dillolíu",
    price: 645,
    unitLabel: "stk.",
    image: require("../../resources/smarettir/lax.jpeg"),
    details: ["Hveiti","Sykur","Smjörlíki","Súrmjólk","Egg","Salt","Lyftiduft","Hjartasalt","Bragðefni","Reyktur lax","Dill","Ólífuolía","Eplaedik"],
  },
  {
    id: "mini-burger-chicken",
    name: "Smá hamborgari",
    description: "með kjúkling, beikon, chilimayo, parmesan og pikkluðum rauðlauk",
    price: 645,
    unitLabel: "stk.",
    image: require("../../resources/smarettir/miniborgara.jpeg"),
    details: ["Hveiti","Sykur","Salt","Olía","Ger","Kjúklingur","Ostur","Beikon","Rauðlaukur","Mæjónes","Egg","Edik","Olía"],
  },
  {
    id: "snitta-brie-parma",
    name: "Snitta",
    description: "með tómatpestó, brie osti, parmaskinku og hunangsristuðum pekanhnetum",
    price: 645,
    unitLabel: "stk.",
    image: require("../../resources/smarettir/brieosta.jpeg"),
    details: ["Hveiti","Vatn","Sykur","Salt","Ger","Olía","Tómatar","Hnetur","Ostur","Hunang","Ólífuolía"],
  },
  {
    id: "soy-chicken-skewer",
    name: "Kjúklingaspjót",
    description: "Soyamarineruð kjúklingaspjót með hvítlaukssósu og parmesan",
    price: 645,
    unitLabel: "stk.",
    image: require("../../resources/smarettir/spjot.jpeg"),
    details: ["Kjúklingur, Hveiti, Soja, Ostur, Mæjónes, Hvítlaukur, Salt, Pipar, Olía, Mjólk, Egg"],
  },
  {
    id: "mini-burger-pulledpork",
    name: "Smá hamborgari",
    description: "með pulled pork og sultuðum rauðlauk",
    price: 645,
    unitLabel: "stk.",
    image: require("../../resources/smarettir/miniborgara.jpeg"),
    details: ["Hveiti","Sykur","Salt","Olía","Ger","Svínakjöt","BBQ sósa","Rauðlaukur","Smjör","Hunang"],
  },
  {
    id: "tomato-bruschetta",
    name: "Ristuð tómatbruchetta",
    description: "með hvítlauk, basil og mozzarella",
    price: 645,
    unitLabel: "stk.",
    image: require("../../resources/smarettir/tomatbruchetta.JPG"),
    details: ["Hveiti","Vatn","Sykur","Salt","Olía","Ger","Hvítlaukur","Basil","Mozzarella","Balsamic edik","Pipar"],
  },
  {
    id: "belgian-waffle-duck",
    name: "Belgísk vaffla",
    description: "með andalæri, teriyaki, vorlauk, pekanhnetum og sesam",
    price: 645,
    unitLabel: "stk.",
    image: require("../../resources/smarettir/vaffla.jpeg"),
    details: ["Hveiti","Egg","Smjör","Mjólk","Sykur","Lyftiduft","Andalæri","Teriyaki sósa","Vorlaukur","Pekanhnetur","Hunang","Salt","Sesamfræ"],
  },
  {
    id: "snitta-beef-truffle",
    name: "Snitta",
    description: "með nauti, trufflusveppamauki og bernaise sósu",
    price: 645,
    unitLabel: "stk.",
    image: require("../../resources/smarettir/naut.jpeg"),
    details: ["Hveiti","Vatn","Sykur","Salt","Ger","Olía","Nautakjöt","Sveppir","Hvítlaukur","Rjómi sýrður","Trufflur","Bernaisesósa","Eggjarauður","Krydd","Nautakraftur"],
  },
  {
    id: "Sætur-biti",
    name: "Sætur biti",
    description: "Blanda af súkkulaði húðuðum jarðaberjum, dumlebitum og lakkrísbitum",
    price: 645,
    unitLabel: "stk.",
    image: require("../../resources/smarettir/sæturbiti.jpeg"),
    details: ["Dumle karamellur","Smjör","Kornflögur","Súkkulaði"],
  },
];

const formatISK = (n) =>
  new Intl.NumberFormat("is-IS", { style: "currency", currency: "ISK" }).format(n);

const clampInt = (v) => {
  const x = Number(v);
  if (!Number.isFinite(x)) return 0;
  return Math.max(0, Math.floor(x));
};

export default function PontunSmarettirModal() {
  const { addItems, items: cartItems } = useCart();
  const cartCount = cartItems.length;

  const [openDetails, setOpenDetails] = useState(() => {
    const init = {};
    BITE_ITEMS.forEach(i => (init[i.id] = false));
    return init;
  });
  const toggleDetails = (id) => setOpenDetails(s => ({ ...s, [id]: !s[id] }));

  const [addedToCart, setAddedToCart] = useState(false);
  const [error, setError] = useState("");
  const [people, setPeople] = useState(MIN_PEOPLE);
  const [qty, setQty] = useState(() => {
    const init = {};
    BITE_ITEMS.forEach(i => (init[i.id] = 0));
    return init;
  });

  const selectedLines = useMemo(() =>
    BITE_ITEMS.filter(i => (qty[i.id] ?? 0) > 0).map(i => ({
      ...i, quantity: qty[i.id], lineTotal: qty[i.id] * i.price,
    })), [qty]);

  const totals = useMemo(() => ({
    totalQty: selectedLines.reduce((s, l) => s + l.quantity, 0),
    totalISK: selectedLines.reduce((s, l) => s + l.lineTotal, 0),
  }), [selectedLines]);

  const perPerson = useMemo(() => {
    const p = Math.max(1, clampInt(people));
    return totals.totalQty / p;
  }, [totals.totalQty, people]);

  const inc = (id) => setQty(q => { const cur = q[id] ?? 0; return { ...q, [id]: cur === 0 ? MIN_PER_BITE : cur + 1 }; });
  const dec = (id) => setQty(q => { const cur = q[id] ?? 0; if (cur === 0) return q; if (cur <= MIN_PER_BITE) return { ...q, [id]: 0 }; return { ...q, [id]: cur - 1 }; });
  const setLine = (id, v) => setQty(q => { const n = clampInt(v); if (n === 0) return { ...q, [id]: 0 }; if (n < MIN_PER_BITE) return { ...q, [id]: MIN_PER_BITE }; return { ...q, [id]: n }; });

  const resetAll = () => {
    setPeople(MIN_PEOPLE);
    setQty(() => { const init = {}; BITE_ITEMS.forEach(i => (init[i.id] = 0)); return init; });
    setError("");
    setAddedToCart(false);
  };

  const handleAddToCart = () => {
    if (clampInt(people) < MIN_PEOPLE) { setError(`Lágmarkspöntun er fyrir ${MIN_PEOPLE} manns.`); return; }
    if (selectedLines.length === 0) { setError("Vinsamlegast veldu a.m.k. eitt atriði."); return; }
    const underMin = selectedLines.find(l => l.quantity < MIN_PER_BITE);
    if (underMin) { setError(`Lágmark á hverja vöru er ${MIN_PER_BITE} stk.`); return; }

    addItems(selectedLines.map(l => ({
      cartId: uuidv4(),
      type: "bite",
      label: `${l.name} (${l.quantity} stk.)`,
      price: l.lineTotal,
      details: { category: "smarettir", id: l.id, name: l.name, description: l.description, price: l.price, quantity: l.quantity, people: clampInt(people) },
    })));
    setQty(() => { const init = {}; BITE_ITEMS.forEach(i => (init[i.id] = 0)); return init; });
    setError("");
    setAddedToCart(true);
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] px-4 py-10">
      <div className="w-full max-w-6xl mx-auto">

        <div className="mb-8 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-stone-900 font-serif">Smáréttir</h1>
            <p className="text-stone-500 font-serif mt-1 text-sm">
              Lágmarkspöntun fyrir <span className="text-stone-800 font-semibold">{MIN_PEOPLE}</span> manns · lágmark {MIN_PER_BITE} stk. á hverja vöru.
            </p>
          </div>
          {cartCount > 0 && (
            <a href="/karfa" className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl border border-[#C29979] text-[#C29979] font-serif text-sm hover:bg-[#C29979]/5 transition-colors">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 7V6a3 3 0 016 0v1M3 7h18l-2 13H5L3 7z"/></svg>
              <span className="bg-[#C29979] text-white text-xs font-bold px-1.5 py-0.5 rounded-full">{cartCount}</span> Karfa
            </a>
          )}
        </div>

        <div className="flex flex-col md:flex-row gap-8 items-start">

          {/* LEFT */}
          <div className="flex-1 min-w-0">

            {error && (
              <div className="mb-5 rounded-xl border border-red-200 bg-red-50 p-3 text-red-700 font-serif text-sm">{error}</div>
            )}

            {/* People count */}
            <div className="bg-amber-50 rounded-2xl border border-amber-200 shadow-sm p-5 mb-6">
              <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wide mb-2">Fjöldi manna</label>
              <div className="flex items-center gap-4">
                <input
                  type="number"
                  min={MIN_PEOPLE}
                  value={people}
                  onChange={e => setPeople(clampInt(e.target.value))}
                  className="w-32 h-11 px-4 bg-white border border-amber-200 rounded-xl text-stone-800 font-serif text-sm focus:outline-none focus:ring-2 focus:ring-[#C29979]/30"
                />
                <p className="text-stone-400 text-sm font-serif">
                  ~{perPerson.toFixed(1)} bitar á mann
                </p>
              </div>
            </div>

            {/* Item grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {BITE_ITEMS.map(item => {
                const q = qty[item.id] ?? 0;
                return (
                  <div key={item.id} className="bg-white rounded-2xl border border-amber-100 shadow overflow-hidden hover:shadow-md transition-shadow flex flex-col">
                    {item.image && (
                      <div className="aspect-[4/3] w-full overflow-hidden">
                        <img src={item.image} alt={item.name} className="h-full w-full object-cover" loading="lazy" />
                      </div>
                    )}

                    <div className="p-4 flex flex-col flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3 className="text-stone-900 font-serif font-semibold leading-snug">{item.name}</h3>
                          {item.description && (
                            <p className="text-stone-500 text-xs mt-1 font-serif leading-snug">{item.description}</p>
                          )}
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-stone-800 font-serif font-semibold text-sm">{item.price} kr</div>
                          <div className="text-stone-400 text-xs font-serif">{item.unitLabel}</div>
                        </div>
                      </div>

                      {Array.isArray(item.details) && item.details.length > 0 && (
                        <div className="mt-3">
                          <button
                            type="button"
                            onClick={() => toggleDetails(item.id)}
                            className="text-xs font-serif text-stone-400 hover:text-[#C29979] underline transition-colors"
                          >
                            {openDetails[item.id] ? "Loka hráefnalista" : "Sjá hráefni"}
                          </button>
                          {openDetails[item.id] && (
                            <div className="mt-2 rounded-xl border border-amber-100 bg-amber-50 p-3">
                              <ul className="list-disc pl-4 space-y-0.5">
                                {item.details.map((d, idx) => (
                                  <li key={idx} className="text-stone-500 text-xs font-serif">{d}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-auto pt-4 flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => dec(item.id)}
                            className="h-9 w-9 rounded-lg border border-amber-200 text-stone-600 hover:border-[#C29979] hover:text-[#C29979] transition-colors text-lg leading-none"
                          >−</button>
                          <input
                            type="number"
                            min={0}
                            value={q}
                            onChange={e => setLine(item.id, e.target.value)}
                            className="h-9 w-16 rounded-lg border border-amber-200 bg-white text-stone-800 text-center font-serif text-sm focus:outline-none focus:ring-2 focus:ring-[#C29979]/30"
                          />
                          <button
                            type="button"
                            onClick={() => inc(item.id)}
                            className="h-9 w-9 rounded-lg border border-amber-200 text-stone-600 hover:border-[#C29979] hover:text-[#C29979] transition-colors text-lg leading-none"
                          >+</button>
                        </div>
                        <div className="text-[#C29979] font-serif font-semibold text-sm">
                          {q > 0 ? formatISK(q * item.price) : ""}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {addedToCart && (
              <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center justify-between">
                <span className="text-emerald-700 font-serif text-sm font-medium">Smáréttir bætt í körfu!</span>
                <a href="/karfa" className="text-[#C29979] font-serif text-sm underline">Fara í körfu →</a>
              </div>
            )}

            <div className="mt-6 flex gap-3">
              <button type="button" onClick={resetAll} className="h-11 px-5 rounded-xl border border-stone-200 text-stone-600 hover:border-stone-300 font-serif uppercase text-sm transition-colors">
                Hreinsa
              </button>
              <button type="button" onClick={handleAddToCart} className="h-11 px-6 rounded-xl bg-[#C29979] text-white font-serif uppercase text-sm hover:bg-[#b08060] transition-colors">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 7V6a3 3 0 016 0v1M3 7h18l-2 13H5L3 7z"/></svg>
                Bæta í körfu
              </button>
            </div>
          </div>

          {/* RIGHT – summary */}
          <aside className="w-full md:w-72 flex-shrink-0 sticky top-6">
            <div className="bg-amber-50 rounded-2xl border border-amber-200 shadow-sm p-5">
              <h2 className="text-stone-900 font-serif font-semibold mb-4">Yfirlit pöntunar</h2>

              <div className="space-y-2 text-sm font-serif border-b border-amber-100 pb-4 mb-4">
                <div className="flex justify-between text-stone-500">
                  <span>Samtals magn</span>
                  <span className="text-stone-800 font-medium">{totals.totalQty} stk.</span>
                </div>
                <div className="flex justify-between text-stone-500">
                  <span>Fjöldi manna</span>
                  <span className="text-stone-800 font-medium">{clampInt(people)}</span>
                </div>
                <div className="flex justify-between text-stone-500">
                  <span>Bitar á mann</span>
                  <span className="text-stone-800 font-medium">{perPerson.toFixed(1)}</span>
                </div>
              </div>

              <div className="flex justify-between font-serif font-semibold text-stone-900">
                <span>Samtals verð</span>
                <span className="text-[#C29979]">{formatISK(totals.totalISK)}</span>
              </div>

              {selectedLines.length > 0 && (
                <div className="mt-4 space-y-2">
                  {selectedLines.map(l => (
                    <div key={l.id} className="flex justify-between items-center text-sm font-serif bg-amber-50 rounded-lg px-3 py-2">
                      <span className="text-stone-700">{l.name} <span className="text-stone-400">×{l.quantity}</span></span>
                      <span className="text-stone-600">{formatISK(l.lineTotal)}</span>
                    </div>
                  ))}
                </div>
              )}

              {selectedLines.length === 0 && (
                <p className="mt-4 text-stone-400 font-serif text-sm">Engin vara valin ennþá...</p>
              )}

              <p className="mt-5 text-stone-300 text-xs font-serif">Verð birtist hér sem summa af völdum smáréttum.</p>
            </div>
          </aside>

        </div>
      </div>
    </div>
  );
}
